import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IToast, LayoutService, ToastService } from 'codex-lib';
import { Observable, of, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { EnvelopeEditTabs } from 'src/app/enum/envelope-edit-tabs';
import { RoutePages } from 'src/app/enum/route-pages';
import { SegmentService } from 'src/app/modules/settings/services/segment.service';
import { EditEnvelope, EditEnvelopeToSend } from '../envelope-edit/model/edit-envelope.model';
import { Envelope } from '../model/envelope';
import { TableSignUserBase } from '../model/table-sign-user-base';
import { EnvelopeService } from './envelope.service';

@Injectable({
  providedIn: 'root'
})
export class EnvelopeEditService {

  public userSegments$ = new Subject<any>();

  private _idAdd = 0;
  private _isAccessAuthentication: boolean;
  private _isOrdered = false;
  private _orderDisabled = false;
  private _envelope: Envelope;
  private _segmentsItems: any[] = [];
  private tableSignUserBase: TableSignUserBase;
  private modelData: Array<any>[3];
  private _includeRubric = false;


  constructor(
    private envelopeService: EnvelopeService,
    private translateService: TranslateService,
    private toastService: ToastService,
    private router: Router,
    private layoutService: LayoutService,
    private segmentService: SegmentService,
  ) {
    this.initModelData();
    this.tableSignUserBase = new TableSignUserBase(translateService);
    this.tableSignUserBase.createInitObject().pipe(take(1)).subscribe(x => x.subscribe(() => this.onInit()));
  }

  get idAdd(): number {
    return this._idAdd;
  }

  set idAdd(value: number) {
    this._idAdd = value;
  }

  get isAccessAuthentication(): boolean {
    return this._isAccessAuthentication;
  }

  set isAccessAuthentication(value: boolean) {
    this._isAccessAuthentication = value;
  }

  get isOrdered(): boolean {
    return this._isOrdered;
  }

  set isOrdered(value: boolean) {
    this._isOrdered = value;
  }

  get orderDisabled(): boolean {
    return this._orderDisabled;
  }

  set orderDisabled(value: boolean) {
    this._orderDisabled = value;
  }

  get includeRubric(): boolean {
    return this._includeRubric;
  }

  set includeRubric(value: boolean) {
    this._includeRubric = value;
  }

  get Envelope(): Envelope {
    return this._envelope;
  }

  onInit() {
  }

  getEnvelope(envelopeId: number): Observable<Envelope> {
    const subject = new Subject<Envelope>();

    if (this._envelope == null || this._envelope.id !== envelopeId) {
      this._envelope = new Envelope();

      this.envelopeService
        .getEnvelopeDetail(envelopeId, false, false, false)
        .subscribe(data => {
          this._envelope = Object.assign({}, data);
          this._isOrdered = this._envelope.isOrdered;
          this._orderDisabled = this._isOrdered ? false : true;
          this._isAccessAuthentication = this._envelope.signatoryAccessAuthentication;
          this._includeRubric = this._envelope.includeRubric;

          subject.next(this._envelope);
        });

      return subject.asObservable();

    } else {
      return of(this._envelope);
    }
  }

  getSegmentsItems(): Observable<any> {
    const segmentsSubject = new Subject<any>();

    if (this._segmentsItems.length == 0) {

      this.segmentService.getSegments(false)
        .subscribe(x => {
          this._segmentsItems = Object.assign([], x.map(y => ({ id: y.id, value: y.name })));

          segmentsSubject.next(this._segmentsItems);
        });

      return segmentsSubject.asObservable();

    } else {
      return of(this._segmentsItems);
    }
  }

  cancel() {
    this.initModelData();
  }

  onSubscriberType(id: number) {
    return this.tableSignUserBase.subscriberType.Get(id);
  }

  onSignatureType(id: number) {
    return this.tableSignUserBase.signatureType.Get(id);
  }

  initModelData() {
    this.modelData = new Array<any>({}, [], []);
    this._envelope = null;
    this._segmentsItems = [];
    this._idAdd = 0;
    this._isOrdered = false;
    this._orderDisabled = false;
  }

  setModelData(modelData: any, tab: number) {
    switch (tab) {
      case 0:
        return this.modelData[tab] = modelData;
      case 1:
        this.modelData[tab] = Object.assign([], modelData);
        break;
      case 2:
        this.modelData[tab] = Object.assign([], modelData);
        break;
      default:
        break;
    }
  }

  getModelData(tab: number): any {
    if (tab < 0) {
      throw new Error('Tab not valid');
    }
    return this.modelData[tab];
  }

  completeEdit() {
    this.layoutService.toggleLoading(true);
    this.sendFormData(this.generateSendEnvelope());
  }

  generateSendEnvelope(): EditEnvelopeToSend {
    const signatoriesToEdit =  this.getModelData(EnvelopeEditTabs.Signatory);
    let signatoriesIdsToRemove;

    if (signatoriesToEdit.signatoriesRemoveds){
      signatoriesIdsToRemove = this._envelope.signatories
        .filter(e => signatoriesToEdit.signatoriesRemoveds.some((x: { id: number }) => x.id === e.id))
        .map(y => y.envelopeSignatoryId);
    }

    const signatories = Array.isArray(signatoriesToEdit?.signatories)
      ? signatoriesToEdit.signatories.map((s: any) => {
          const isTempId = typeof s?.id === 'string' && s.id.startsWith('temp-');
          return {
            ...s,
            id: isTempId ? null : s.id
          };
        })
      : [];

    const editEnvelope = new EditEnvelope();
    editEnvelope.setInformation((this.getModelData(EnvelopeEditTabs.Information) as FormGroup).value);
    editEnvelope.setSignatory(signatories, signatoriesIdsToRemove);
    editEnvelope.setIsOrdered(this.isOrdered);
    editEnvelope.setIsAccessAuthentication(this.isAccessAuthentication);
    return editEnvelope.generateSend();
  }

  sendFormData(envelope) {
    this.envelopeService.editFull(envelope.id, envelope).subscribe(
      next => {
        this.envelopeSendFormSuccess();
      },
      error => {
        this.envelopeSendFormError(error);
      }
    );
  }

  envelopeSendFormError(error) {
    this.layoutService.toggleLoading(false);
    console.log('error ' + error);
  }

  envelopeSendFormSuccess() {
    this.layoutService.toggleLoading(false);
    const toast: IToast = {
      text: this.translateService.instant('envelope.review.toast.sendSuccess')
      , autoHide: true, type: 'success'
    };
    this.toastService.createNewToast(toast);
    this.layoutService.toggleButtonCofirmGoBack(true);
    this.layoutService.toggleViewer(false);
    this.router.navigate([RoutePages.Root]).then(x => this.initModelData());
  }

  getSubjectUserSegments(): Observable<any> {
    return this.userSegments$.asObservable();
  }

  setSubjectUserSegments(data: any) {
    this.userSegments$.next(data);
  }
}
